import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef } from 'react';
import { toPersianDigits } from '@bookora/shared';
import { Form as AntdForm, Input as AntdInput } from 'antd';
import { Icon } from '../icons/icon.js';
/**
 * ════════════════════════════════════════════════════════════════════════════
 *  FORM & TEXT INPUTS
 * ════════════════════════════════════════════════════════════════════════════
 *
 * ── FORM, AND WHAT RTL ACTUALLY COSTS HERE ──────────────────────────────────
 *
 * Very little, and that is worth writing down so nobody "fixes" it. AntD v5's
 * form styles are logical throughout: the label column is `text-align: start`
 * (`end` in the horizontal layout, which is still the label's own inline end),
 * the required asterisk is placed with `margin-inline-end`, and
 * `.ant-form-item-explain` inherits the item's direction so an error message
 * starts where its field starts. `test/antd-rtl.test.tsx` renders a required
 * field, submits it empty and asserts each of those against the real DOM,
 * because they are the ones a theme override can quietly break.
 *
 * So `Form` is AntD's, unchanged. The one default worth having —
 * `scrollToFirstError` — is set on the `ConfigProvider` in
 * `theme/theme-provider.tsx` instead of here, because a wrapper only reaches
 * the forms that remember to use it and the provider reaches every one,
 * including the ones AntD renders itself.
 *
 * ── INPUTS ──────────────────────────────────────────────────────────────────
 *
 * `Input.Password`'s reveal toggle is the only text-input affordance with a
 * glyph of its own; it comes from the registry, and `active` marks the revealed
 * state — which is exactly what `active` is for.
 *
 * Numerals, digit shaping and bidi isolation are NOT decided here. They belong
 * to `@bookora/shared` and to the bidi primitives, and an input that quietly
 * transliterated its own value would be a trap for phone numbers and national
 * IDs. See `components/money.tsx` and `components/phone-input.tsx` for the two
 * fields that do format, on purpose, in one place each.
 */
// The annotations are load-bearing for `tsc --declaration`: without them the
// emitted `.d.ts` names `rc-field-form`'s internals, which are not a dependency
// of this package and therefore not resolvable by its consumers.
export const Form = AntdForm;
/**
 * The clear button is a bare `<button>` around this icon, so the icon's label
 * IS the button's accessible name. Decorative (the registry default), the
 * button had none at all; AntD's own glyph named it «close-circle».
 */
const CLEAR_ICON = _jsx(Icon, { name: "close-square", size: 16, label: "\u067E\u0627\u06A9 \u06A9\u0631\u062F\u0646" });
/**
 * ── `allowClear` DRAWS AntD's OWN ICON SET, AND THE SOURCE SCAN CANNOT SEE IT ─
 *
 * `<Input allowClear />` renders `<span class="anticon anticon-close-circle">`
 * from inside `rc-input`. No file imports `@ant-design/icons`, so both the lint
 * rule and `test/antd-import-guard.test.ts` pass — and a second icon set is on
 * screen anyway. Found by counting `.anticon` nodes in Chrome on the console's
 * clinic list, not by reading source.
 *
 * `ConfigProvider`'s `input.allowClear` cannot fix it: antd resolves the local
 * prop as `allowClear ?? contextAllowClear`, so a call site that writes
 * `allowClear` at all shadows the context entirely — and setting the context to
 * an object would switch clearing ON for every input in the product, which is a
 * behaviour change, not an icon change.
 *
 * So the normalisation happens here: `true` becomes `{ clearIcon }`, an object
 * keeps whatever icon it already chose, and `false`/absent stays untouched.
 */
const withRegistryClearIcon = (allowClear) => {
    if (allowClear === true)
        return { clearIcon: CLEAR_ICON };
    if (allowClear && typeof allowClear === 'object' && allowClear.clearIcon === undefined) {
        return { ...allowClear, clearIcon: CLEAR_ICON };
    }
    return allowClear;
};
/**
 * ── `dir="ltr"` ON A FIELD, MADE TO ACTUALLY WORK ───────────────────────────
 *
 * `dir` is not a style. It is an attribute the UA stylesheet turns into
 * `direction: ltr`, and **any author rule beats a UA rule**. AntD, under
 * `ConfigProvider direction="rtl"`, injects `.ant-input-rtl { direction: rtl }`
 * and `.ant-input-affix-wrapper-rtl { direction: rtl }` into `<head>` at
 * runtime — so a field carrying nothing but the attribute renders RTL anyway,
 * and a grouped value inside it has its groups reversed by the bidi algorithm:
 * a postal code, a landline «۰۲۱ ۸۸۷۷ ۶۶۵۵», a bank reference. The failure is
 * silent and only visible with a real value on a real screen.
 *
 * `components/phone-input.tsx` found this the hard way and answered it with
 * three overlapping declarations. Thirteen other fields across the two panels
 * then wrote the bare attribute — the thing that file says does not work — and
 * nine added `style={{ textAlign: 'start' }}`, where `start` is resolved
 * against the SURVIVING `direction: rtl` and therefore means *right*.
 *
 * That is one wrong assumption repeated, so the answer belongs here rather than
 * at thirteen call sites: asking for `dir="ltr"` gets the whole treatment.
 *
 *  1. the attribute stays, for assistive tech and for `el.dir`;
 *  2. `.bk-ltr-field` (`styles/ui.css`) restates it at AUTHOR level, at a
 *     specificity that beats AntD's `-rtl` rule — on the `<input>` AND on the
 *     affix wrapper, because `className` lands on whichever of the two exists
 *     and that changes with a prop;
 *  3. an inline `direction` on the real `<input>` via `styles.input`, which no
 *     stylesheet order can undo.
 *
 * A call site's own `className`/`classNames`/`styles` are preserved, and its
 * `styles.input.direction` wins if it sets one — this adds a default, it does
 * not seize the field.
 */
const LTR_FIELD_CLASS = 'bk-ltr-field';
/**
 * AntD 6 accepts `classNames`/`styles` either as an object or as a function of
 * the props. A wrapper that merges its own defaults into them needs the object
 * form, so the function form is resolved here first — against the same props
 * AntD would have called it with.
 */
export function resolveInputSemantics(props) {
    return {
        classNames: typeof props.classNames === 'function' ? props.classNames({ props }) : props.classNames,
        styles: typeof props.styles === 'function' ? props.styles({ props }) : props.styles,
    };
}
function withLtrField(props) {
    if (props.dir !== 'ltr')
        return props;
    const join = (extra) => [LTR_FIELD_CLASS, extra].filter(Boolean).join(' ');
    const { classNames, styles } = resolveInputSemantics(props);
    return {
        ...props,
        className: join(props.className),
        classNames: { ...classNames, input: join(classNames?.input) },
        styles: {
            ...styles,
            input: { direction: 'ltr', unicodeBidi: 'isolate', ...styles?.input },
        },
    };
}
/**
 * AntD's counter prints `0 / 2000` in ASCII under every textarea with
 * `showCount`. `true` becomes the same counter in Persian digits; an object
 * (which always carries its own `formatter`) is left alone.
 */
function withPersianCount(showCount) {
    if (!showCount)
        return showCount;
    // AntD's object form requires a `formatter`, so an object is the caller's own.
    if (typeof showCount === 'object')
        return showCount;
    return {
        formatter: ({ count, maxLength }) => maxLength === undefined
            ? toPersianDigits(count)
            : `${toPersianDigits(count)} از ${toPersianDigits(maxLength)}`,
    };
}
/**
 * Text input. Identical to AntD's except that its clear affordance comes from
 * the registry — see {@link withRegistryClearIcon} — and that `dir="ltr"`
 * survives the RTL provider — see {@link withLtrField}.
 */
export const Input = Object.assign(forwardRef(function Input(props, ref) {
    const next = withLtrField(props);
    return _jsx(AntdInput, { ...next, ref: ref, allowClear: withRegistryClearIcon(next.allowClear) });
}), {
    Password: AntdInput.Password,
    TextArea: forwardRef(function TextArea(props, ref) {
        return (_jsx(AntdInput.TextArea, { ...props, ref: ref, showCount: withPersianCount(props.showCount) }));
    }),
    Search: AntdInput.Search,
    Group: AntdInput.Group,
    OTP: AntdInput.OTP,
});
/** Password field whose reveal toggle uses the registry's eye pair. */
export const PasswordInput = forwardRef(function PasswordInput(props, ref) {
    return (_jsx(AntdInput.Password, { ...withLtrField(props), ref: ref, iconRender: (visible) => (_jsx(Icon, { name: visible ? 'eye' : 'eye-off', size: 16, active: visible })) }));
});
/** Search field with the registry's magnifier on the reading-start edge. */
export const SearchInput = forwardRef(function SearchInput({ prefix, ...rest }, ref) {
    const next = withLtrField(rest);
    return (_jsx(AntdInput, { ...next, ref: ref, allowClear: withRegistryClearIcon(next.allowClear ?? true), prefix: prefix === undefined ? _jsx(Icon, { name: "search", size: 16 }) : prefix }));
});
//# sourceMappingURL=inputs.js.map